import { applicationSettings } from '../../settings/application';
import { setFilters } from '../productMenu/productMenuActions.ts';
import swiperService from '../../services/swiper';
import { actionSetProducts } from '../products/productsActions.ts';
import { formatProductsMeasurements } from '../products/productsThunks.ts';
import productService from '../../services/products/index';
import { actionSetRawProducts } from '../rawData/rawDataActions';
import { actionSetSwiperSubFilter } from '../productMenu/productMenuActions';
import { selectDefaultSubFilterIndex } from '../productMenu/productMenuSelectors';
import { getAllPartsAsIdsFromItems } from '../products/productsHelpers';

const fileUrl = `./configs/${applicationSettings.applicationName.toLowerCase()}`;

function request(url, fallback = {}) {
  return fetch(url, { credentials: 'same-origin' })
    .then(res => res.json())
    .catch(err => {
      console.error(err);
      return fallback;
    });
}

export default function loadProductMenu() {
  const extractFilePart = (files, part) => files.find(item => part in item);

  /**
   * Generate products object,
   * @param ids
   * @param product
   * @returns {*&{relations}}
   */
  const generateProductObject = (ids, product) => ({
    ...product,
    relations: ids.filter(id => product.id !== id),
  });

  /**
   * Add relations array to products object
   * @param ids
   */
  const createProductsArray = ids =>
    ids.reduce((acc, itemId) => {
      const product = productService.getProduct(itemId);
      return product ? [...acc, generateProductObject(ids, product)] : acc;
    }, []);

  /**
   * Get items by id
   * @param itemsIds
   */
  const getItemsByIds = itemsIds =>
    itemsIds.reduce((acc, curr) => [...acc, ...createProductsArray(curr)], []);

  /**
   * Convert items array to object with product ID as key
   * @returns {function(*, *): *}
   */
  const convertToIdObject = (acc, curr) => ({
    ...acc,
    [curr.id]: curr,
  });

  /**
   * Validates a filter by making sure it has at least on valid product
   * @param products
   */
  const validateFilter = ({ products }) =>
    products.flat().filter(product => productService.getProduct(product)?.valid)
      .length;

  /**
   * Removes invalid products
   * @param products
   * @returns {*}
   */
  const removeInvalidProductIds = products =>
    products.reduce((validProductsIds, productIds) => {
      const validProducts = productIds.filter(
        id => !!productService.getProduct(id)
      );

      return validProducts.length
        ? [...validProductsIds, validProducts]
        : validProductsIds;
    }, []);

  const subFilterHasItems = (filter, rawItems) => filterOption => {
    const subFilterKey = filter.subFilter.key;

    const items = filter.products.reduce((acc, [id]) => {
      if (!id) return acc;

      const product = rawItems[id];
      if (id && productService.getProduct(id)) return [...acc, product];

      return acc;
    }, []);

    return !!items.filter(product => {
      return product[subFilterKey] === filterOption.value;
    }).length;
  };

  /**
   * Adds a property (hasItems) to subfilter options to indicate
   * whether they have products or are empty.
   * @param {*} filter
   * @param {*} items
   * @returns {*}
   */
  const flagSubfilterKeys = (filter, items) => {
    if (!filter.hasOwnProperty('subFilter')) return filter;

    const subFilterOptionHasItems = subFilterHasItems(filter, items);

    return {
      ...filter.subFilter,
      options: filter.subFilter.options.map(option => ({
        ...option,
        hasItems: subFilterOptionHasItems(option),
      })),
    };
  };

  /**
   * Function for manipulating individual filters. eg. remove/flag unwanted or disabled products
   * @param filter
   * @param items
   * @returns {*&{products}}
   */
  const cureFilter = (filter, items) => {
    return {
      ...filter,
      products: removeInvalidProductIds(filter.products),
      subFilter: flagSubfilterKeys(filter, items),
    };
  };

  /**
   * Returns all valid filters
   * @param files
   */
  const getValidFilters = files =>
    extractFilePart(files, 'filters').filters.filter(validateFilter);

  /**
   * Returns all items as object
   */
  const getItemsObject = itemsIds =>
    getItemsByIds(itemsIds).reduce(convertToIdObject, {});

  /**
   * Returns all item id's in a flat array
   * @param filters
   * @returns {*}
   */
  const getItemsIds = filters =>
    filters.reduce((acc, { products }) => [...acc, products], []).flat();

  /**
   * Convert filters to object with name as key
   * @param items
   * @returns {function(*, *=): *}
   */
  const convertFiltersToObject = items => (acc, curr) => ({
    ...acc,
    [curr.name]: cureFilter(curr, items),
  });

  /**
   * Sets thumbnails
   * @param files
   */
  const setThumbnails = files => {
    const thumbnails = extractFilePart(files, 'thumbnails').thumbnails;
    swiperService.init(thumbnails);
  };

  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      const files = [
        `${fileUrl}/productMenu.json`,
        `${fileUrl}/thumbnails.json`,
      ];

      Promise.all(files.map(request)).then(files => {
        setThumbnails(files);

        const filters = getValidFilters(files);
        const itemsIds = getItemsIds(filters);
        const items = getItemsObject(itemsIds);
        const partIds = getAllPartsAsIdsFromItems(items);
        const parts = getItemsObject(partIds);

        const curedFilters = filters.reduce(convertFiltersToObject(items), {});
        const products = dispatch(formatProductsMeasurements(itemsIds));

        dispatch(actionSetProducts(products));
        dispatch(actionSetRawProducts({ ...items, ...parts }));
        dispatch(setFilters(curedFilters, { nonInteraction: true }));
        dispatch(
          actionSetSwiperSubFilter(selectDefaultSubFilterIndex(getState()))
        );
        resolve();
      });
    });
  };
}
